import {
  isConfidential,
  isFromDenmark,
  isFromFinland,
  isFromNorway,
  isFromPoland,
  isFromSweden,
  isRecycling,
} from '@sr/utils';

const checkFunctions = {
  isSwedish: isFromSweden,
  isNorwegian: isFromNorway,
  isDanish: isFromDenmark,
  isFinnish: isFromFinland,
  isPolish: isFromPoland,
  isConfidential: isConfidential,
  isRecycling: isRecycling,
} as const;

/**
 * Analyzes a list of dwKeys to determine country associations
 *
 * @param partners - An array of dwKeys to be analyzed.
 * @param checkIfPartner - An optional array of which countries to check with default properties set to false. If not supplied, it defaults to:
 *                  ['isSwedish', 'isNorwegian', 'isDanish', 'isFinnish', 'isPolish', 'isConfidential', 'isRecycling']
 * @returns An object containing boolean flags indicating:
 *            - isSwedish: true if a dwKey corresponding to Sweden was found.
 *            - isNorwegian: true if a dwKey corresponding to Norway was found.
 *            - isDanish: true if a dwKey corresponding to Denmark was found.
 *            - isFinnish: true if a dwKey corresponding to Finland was found.
 *            - isPolish: true if a dwKey corresponding to Poland was found.
 *            - isConfidential: true if a confidential dwKey was found.
 *            - isRecycling: true if a recycling dwKey was found.
 */
export const partnersIsFromCountry = <T extends (keyof typeof checkFunctions)[]>(
  partners: string[],
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  checkIfPartner: T = Object.keys(checkFunctions) as T,
): Record<T[number], boolean> => {
  const result = createObjectFromKeys(checkIfPartner, false);

  for (const dwKey of partners) {
    for (const key of checkIfPartner) {
      if (!result[key] && checkFunctions[key](dwKey)) {
        result[key] = true;
      }
    }

    if (Object.values(result).every(Boolean)) {
      break;
    }
  }

  return result;
};

const createObjectFromKeys = <T extends string, V>(keys: T[], value: V): Record<T, V> => {
  return keys.reduce<Record<T, V>>(
    (acc, key) => {
      acc[key] = value;

      return acc;
    },
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as Record<T, V>,
  );
};
